import { createContext, useContext, useState, useCallback, useMemo, type ReactNode } from 'react';
import type { Appointment, Visitor } from '@/types';
import type { KioskPushPayload } from '../types/kioskPush';

// Parameters received when starting safety training via SignalR
export interface TriggeredTrainingParams {
  safetyTrainingId: string;
  personId: string;
  identityId: string;
  personSurname: string;
  personGivenName: string;
  traceId: string; // appointmentId
  userId: string; // receptionist who triggered the training
}

// GAP-04: both KioskCheckInAction (self-check-in) and KioskLayout's onStartSafetyTraining
// (reception-triggered SignalR) set triggeredTraining/selectedVisitor identically before
// navigating to the safety-training screen — neither field alone tells the two journeys apart.
// This is the origin marker KioskSignature reads to decide whether IT owns re-invoking the
// check-in once the briefing is done (self-check-in) or whether reception owns that follow-up
// call from its own UI (unchanged reception behaviour). Present only for the self-check-in
// journey; null for every other path, including a direct visit to any /kiosk/checkin/* route.
export interface SelfCheckInOrigin {
  appointmentId: string;
  visitorId: string;
}

interface KioskState {
  kioskKey: string | null; // Kiosk identifier for SignalR registration
  isAuthenticated: boolean; // True when kiosk has authenticated via BFF ROPC flow
  selectedAppointment: Appointment | null;
  selectedVisitor: Visitor | null;
  safetyTrainingId: string | null;
  safetyTrainingCompleted: boolean;
  dsgvoAccepted: boolean;
  dataRetentionMonths: number; // DSGVO data retention period (1, 3, 6, or 12 months)
  signatureBase64: string | null;
  triggeredTraining: TriggeredTrainingParams | null; // Set when training is triggered via SignalR
  // Phase 34 plan 34-01: the most recent workflowPush received over the kiosk SignalR hub — the
  // payload KioskWaitingForInput dispatches on. No other plan in this phase may add a field here;
  // every kiosk-context field the phase needs is added by this plan.
  activePush: KioskPushPayload | null;
  // GAP-04: see SelfCheckInOrigin's own doc comment.
  selfCheckInOrigin: SelfCheckInOrigin | null;
}

interface KioskContextValue extends KioskState {
  setKioskKey: (key: string | null) => void;
  setAuthenticated: (authenticated: boolean) => void;
  setSelectedAppointment: (appointment: Appointment | null) => void;
  setSelectedVisitor: (visitor: Visitor | null) => void;
  setSafetyTrainingId: (id: string | null) => void;
  setSafetyTrainingCompleted: (completed: boolean) => void;
  setDsgvoAccepted: (accepted: boolean) => void;
  setDataRetentionMonths: (months: number) => void;
  setSignatureBase64: (signature: string | null) => void;
  setTriggeredTraining: (params: TriggeredTrainingParams | null) => void;
  setActivePush: (payload: KioskPushPayload | null) => void;
  setSelfCheckInOrigin: (origin: SelfCheckInOrigin | null) => void;
  // Derived from activePush?.nodeExecutionId — convenience accessor for screens that only need the id.
  activeNodeExecutionId: string | null;
  reset: () => void;
}

// Storage keys for kiosk persistence
const KIOSK_KEY_STORAGE = 'maja-kiosk-key';
// Quick task 260907-hmv: this literal MUST stay byte-identical to
// `kioskSessionStore.ts`'s own `KIOSK_AUTH_STORAGE` constant — that store's `markExpired()`
// removes this same key when a kiosk session cannot be renewed, so the two constants are not
// imported from a shared module but must be kept in sync by hand if either is renamed.
const KIOSK_AUTH_STORAGE = 'maja-kiosk-authenticated';

const getStoredKioskKey = (): string | null => {
  try {
    return localStorage.getItem(KIOSK_KEY_STORAGE);
  } catch {
    return null;
  }
};

const getStoredAuthenticated = (): boolean => {
  try {
    return localStorage.getItem(KIOSK_AUTH_STORAGE) === 'true';
  } catch {
    return false;
  }
};

const initialState: KioskState = {
  kioskKey: getStoredKioskKey(),
  isAuthenticated: getStoredAuthenticated(),
  selectedAppointment: null,
  selectedVisitor: null,
  safetyTrainingId: null,
  safetyTrainingCompleted: false,
  dsgvoAccepted: false,
  dataRetentionMonths: 1, // Default: 1 month (like Blazor)
  signatureBase64: null,
  triggeredTraining: null,
  activePush: null,
  selfCheckInOrigin: null,
};

const KioskContext = createContext<KioskContextValue | null>(null);

export function KioskProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<KioskState>(initialState);

  const setKioskKey = useCallback((key: string | null) => {
    setState((prev) => ({ ...prev, kioskKey: key }));
    // Persist to localStorage
    try {
      if (key) {
        localStorage.setItem(KIOSK_KEY_STORAGE, key);
      } else {
        localStorage.removeItem(KIOSK_KEY_STORAGE);
      }
    } catch {
      // Ignore storage errors
    }
  }, []);

  const setAuthenticated = useCallback((authenticated: boolean) => {
    setState((prev) => ({ ...prev, isAuthenticated: authenticated }));
    try {
      if (authenticated) {
        localStorage.setItem(KIOSK_AUTH_STORAGE, 'true');
      } else {
        localStorage.removeItem(KIOSK_AUTH_STORAGE);
      }
    } catch {
      // Ignore storage errors
    }
  }, []);

  const setSelectedAppointment = useCallback((appointment: Appointment | null) => {
    setState((prev) => ({ ...prev, selectedAppointment: appointment }));
  }, []);

  const setSelectedVisitor = useCallback((visitor: Visitor | null) => {
    setState((prev) => ({ ...prev, selectedVisitor: visitor }));
  }, []);

  const setSafetyTrainingId = useCallback((id: string | null) => {
    setState((prev) => ({ ...prev, safetyTrainingId: id }));
  }, []);

  const setSafetyTrainingCompleted = useCallback((completed: boolean) => {
    setState((prev) => ({ ...prev, safetyTrainingCompleted: completed }));
  }, []);

  const setDsgvoAccepted = useCallback((accepted: boolean) => {
    setState((prev) => ({ ...prev, dsgvoAccepted: accepted }));
  }, []);

  const setDataRetentionMonths = useCallback((months: number) => {
    setState((prev) => ({ ...prev, dataRetentionMonths: months }));
  }, []);

  const setSignatureBase64 = useCallback((signature: string | null) => {
    setState((prev) => ({ ...prev, signatureBase64: signature }));
  }, []);

  const setTriggeredTraining = useCallback((params: TriggeredTrainingParams | null) => {
    setState((prev) => ({ ...prev, triggeredTraining: params }));
  }, []);

  const setActivePush = useCallback((payload: KioskPushPayload | null) => {
    setState((prev) => ({ ...prev, activePush: payload }));
  }, []);

  const setSelfCheckInOrigin = useCallback((origin: SelfCheckInOrigin | null) => {
    setState((prev) => ({ ...prev, selfCheckInOrigin: origin }));
  }, []);

  const activeNodeExecutionId = useMemo(
    () => state.activePush?.nodeExecutionId ?? null,
    [state.activePush]
  );

  const reset = useCallback(() => {
    // Reset everything except kioskKey (which should persist)
    setState((prev) => ({
      ...initialState,
      kioskKey: prev.kioskKey,
    }));
  }, []);

  return (
    <KioskContext.Provider
      value={{
        ...state,
        setKioskKey,
        setAuthenticated,
        setSelectedAppointment,
        setSelectedVisitor,
        setSafetyTrainingId,
        setSafetyTrainingCompleted,
        setDsgvoAccepted,
        setDataRetentionMonths,
        setSignatureBase64,
        setTriggeredTraining,
        setActivePush,
        setSelfCheckInOrigin,
        activeNodeExecutionId,
        reset,
      }}
    >
      {children}
    </KioskContext.Provider>
  );
}

export function useKiosk() {
  const context = useContext(KioskContext);
  if (!context) {
    throw new Error('useKiosk must be used within a KioskProvider');
  }
  return context;
}
